import { FormSectionHandler } from '../entity.handler';
import { DeviceTypeIOPropertiesDictionary, DeviceTypeResponse } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { ArrayComboValueHandlers, CheckListField, ComboField, SectionField, } from '../utils';
import { DeviceParametersResponse, DeviceParametersUpdateRequest, getDeviceTypeIdFromBulk } from './parameters.section';
import { uniq } from 'lodash';
import { DeviceAccessoryResponse } from './accessory.section';

interface EditorValues {
    mode: string;
    whitelist: string[];
}



export class DeviceIOWhitelistSectionHandler implements FormSectionHandler<DeviceParametersResponse, EditorValues> {

    type = 'form';
    icon = 'list-check';
    bulk = true;
    hidden = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private applyUpdateCallback: (entity: any, payload: any) => Promise<DeviceParametersResponse>, 
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.DEVICE.IO_WHITELIST.${key}`, values);
    }

    applyUpdate(entity: DeviceParametersResponse, payload: DeviceParametersUpdateRequest): Promise<DeviceParametersResponse> {
        return this.applyUpdateCallback(entity, payload);
    }

    async mapEntityToEditor(entity: DeviceParametersResponse): Promise<EditorValues> {
        const deviceTypeId = getDeviceTypeIdFromBulk(entity ? [entity] : this.admin.selectedItems);
        const deviceType = await this.admin.getCached(`getDeviceType/${deviceTypeId}/${this.ownerId}`, api => api.entities.getDeviceType(deviceTypeId, this.ownerId));
        return {
            mode: entity.parameters?.io_whitelist?.length > 0 ? 'custom' : 'default',
            whitelist: entity.parameters?.io_whitelist || getIoKeys(deviceType, entity, false),
        };
    }

    async mapEditorToEntity(_entity: DeviceParametersUpdateRequest, values: EditorValues): Promise<DeviceParametersUpdateRequest> {
        return {
            parameters: {
                io_whitelist: values.mode === 'default' ? null : values.whitelist
            }
        };
    }

    async getFields(entity: DeviceParametersResponse): Promise<SectionField[]> {

        const deviceTypeId = getDeviceTypeIdFromBulk(entity ? [entity] : this.admin.selectedItems);
        if (deviceTypeId) {
            this.hidden = false;

            const deviceType = await this.admin.getCached(`getDeviceType/${deviceTypeId}/${this.ownerId}`, api => api.entities.getDeviceType(deviceTypeId, this.ownerId));
            const ios = getIoKeys(deviceType, entity, true);

            return [
                ComboField('mode', this.translate('FIELDS.MODE'), true, [
                    { key: 'default', value: this.translate('FIELDS.MODE_DEFAULT') },
                    {
                        key: 'custom', value: this.translate('FIELDS.MODE_CUSTOM'),
                        fields: [
                            CheckListField('whitelist', this.translate('FIELDS.WHITELIST'), true, ios.map(x => ({ key: x, value: x })), ArrayComboValueHandlers),
                        ]
                    },
                ]),
            ];
        } else {
            this.hidden = true;
            return Promise.resolve([]);
        }
    }

}

export function getIoKeys(
    deviceType: DeviceTypeResponse,
    device: DeviceParametersResponse & DeviceAccessoryResponse,
    includeOptional: boolean,
    ioTypes = ['analog_input', 'digital_input', 'digital_output', 'can_input', 'counter_input', 'temperature_input', 'value_input', 'camera_input']
): string[] {
    
    let result = [];

    const iterateIOCapabilities = (ioProperties: DeviceTypeIOPropertiesDictionary) => {
        const ios = Object.keys(ioProperties);
        for (var io of ios) {
            if (includeOptional || !ioProperties[io].optional) {
                result.push(io);
            }
        }
    };

    for (var ioType of ioTypes) {
        iterateIOCapabilities(deviceType.ioCapabilities[ioType] || {});
        // make sure we include any custom inputs from any accessories that may have been added
        for (const accessoryId of Object.keys(device?.accessories || {})) {
            iterateIOCapabilities(deviceType.accessories[accessoryId]?.ioCapabilities[ioType] || {});
        }
    }

    result.push(...(device?.parameters?.bitmaps || []).filter(x => ioTypes.includes(x.target_type)).map(x => x.target_input));

    result = uniq(result);
    result.sort();
    return result;
}


export function getDeviceIOWhitelist(
    deviceType: DeviceTypeResponse,
    device: DeviceParametersResponse,
    ioType: string,
): string[] {

    const allInputs = getIoKeys(deviceType, device, true, [ioType]);
    const defaultInputs = getIoKeys(deviceType, device, false, [ioType]);
    let customInputs = [];
    if (device?.parameters?.io && device?.parameters?.io[ioType]) {
        customInputs = Object.keys(device.parameters.io[ioType]).map(key => device.parameters.io[ioType][key].input).filter(x => !allInputs.includes(x));
    }
    const whitelist = device?.parameters?.io_whitelist || [];
    if (whitelist.length) {
        // if we have a whitelist, only items in the whitelist and custom inputs are valid
        return [...allInputs.filter(x => whitelist.includes(x)), ...customInputs];
    } else {
        // if we don't have a whitelist, then all inputs in the default and custom inputs are valid
        return [...defaultInputs, ...customInputs];
    }

}